import { AxiosError, AxiosResponse } from 'axios';
import { UseMutationOptions, useMutation, useQuery } from 'react-query';

import { client } from '@frontend/features/common/services';
import { QueryKeys } from '@frontend/features/common/types';
import {
  CreateWorkflowDto,
  GenericGetFilterDto,
  StepConnectionsResponseDto,
  StepOptionsResponseDto,
  UpdateWorkflowDto,
  WorkflowByIdResponseDto,
  WorkflowResponseDto,
  PagedResponse,
} from '@bandido/shared/dtos';

// WORKFLOWS FEATURE QUERIES --

interface useGetAllWorkflowsQueryProps extends GenericGetFilterDto {
  onErrorCallback?: (_e: AxiosError) => void;
}

const getWorkflowById = (id: string) => client.get<WorkflowByIdResponseDto>(`/workflows/${id}`);
export const useGetWorkflowById = (id?: string) =>
  useQuery({
    queryKey: [QueryKeys.getWorkflowById, id],
    queryFn: () => getWorkflowById(id!),
    enabled: Boolean(id),
    refetchOnWindowFocus: false,
    select: (data: AxiosResponse<WorkflowByIdResponseDto>) => data.data,
  });
export const useGetAllWorkflowsQuery = ({
  currentPage,
  onErrorCallback,
  pageSize,
  search,
  agencyId,
}: useGetAllWorkflowsQueryProps) =>
  useQuery({
    queryKey: [QueryKeys.getAllWorkflows, currentPage, pageSize, search, agencyId],
    queryFn: async (): Promise<PagedResponse<WorkflowResponseDto> | undefined> => {
      try {
        const params: Record<string, unknown> = {
          currentPage,
          pageSize,
          search,
          agencyId,
        };
        const workflows: AxiosResponse<PagedResponse<WorkflowResponseDto>, any> = await client.get('workflows', {
          params,
        });

        return workflows.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
  });

// WORKFLOWS FEATURE MUTATIONS --

const createWorkflow = async (data: CreateWorkflowDto) => client.post<WorkflowResponseDto>('workflows', data);
export const useCreateWorkflowMutation = (
  options?:
    | Omit<
        UseMutationOptions<AxiosResponse<WorkflowResponseDto, any>, unknown, CreateWorkflowDto, unknown>,
        'mutationFn'
      >
    | undefined,
) => useMutation(createWorkflow, options);

const updateWorkflow = async (id: string, data: UpdateWorkflowDto) =>
  client.patch<WorkflowResponseDto>(`workflows/${id}`, data);
export const useUpdateWorkflowMutation = (
  id: string,
  options?:
    | Omit<
        UseMutationOptions<AxiosResponse<WorkflowResponseDto, any>, unknown, UpdateWorkflowDto, unknown>,
        'mutationFn'
      >
    | undefined,
) => useMutation((data) => updateWorkflow(id, data), options);

const updateWorkflowMetadata = async (id: string, data: UpdateWorkflowDto) =>
  client.patch<WorkflowResponseDto>(`workflows/metadata/${id}`, data);
export const useUpdateWorkflowMetadataMutation = (
  id: string,
  options?:
    | Omit<
        UseMutationOptions<AxiosResponse<WorkflowResponseDto, any>, unknown, UpdateWorkflowDto, unknown>,
        'mutationFn'
      >
    | undefined,
) => useMutation((data) => updateWorkflowMetadata(id, data), options);

// OTHER FEATURES QUERIES --

const getStepConnections = async (id: string) => client.get<StepConnectionsResponseDto>(`/steps/${id}/connections`);

interface useGetWorkflowsByAgencyProps extends GenericGetFilterDto {
  onErrorCallback?: (_e: AxiosError) => void;
}
interface useGetStepsByManyWorkflowIdsProps {
  workflowIds: string[];
  search: string;
}

export const useGetWorkflowsByAgency = (params?: useGetWorkflowsByAgencyProps | undefined) => {
  const { currentPage = 1, pageSize = 1000, search, onErrorCallback, agencyId } = params || {};

  return useQuery({
    queryKey: [QueryKeys.getAllWorkflows, currentPage, pageSize, search, agencyId],
    queryFn: async (): Promise<PagedResponse<WorkflowResponseDto> | undefined> => {
      try {
        const users: AxiosResponse<PagedResponse<WorkflowResponseDto>, any> = await client.get(
          `workflows/agency/${agencyId}`,
          {
            params: {
              currentPage,
              pageSize,
              search,
            },
          },
        );

        return users.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
    enabled: Boolean(agencyId),
  });
};
export const useGetWorkflowSteps = (id?: string) =>
  useQuery({
    queryKey: [QueryKeys.getWorkflowSteps, id],
    queryFn: () => client.get<StepOptionsResponseDto[]>(`/workflows/${id}/steps`),
    enabled: Boolean(id),
    select: (data: AxiosResponse<StepOptionsResponseDto[]>) => data.data,
  });
export const useGetStepsByManyWorkflowIds = ({ workflowIds, search }: useGetStepsByManyWorkflowIdsProps) =>
  useQuery({
    queryKey: [QueryKeys.getStepsByManyWorkflowIds, workflowIds],
    queryFn: () =>
      client.get<StepOptionsResponseDto[]>('/workflows/steps', {
        params: {
          workflowIds: workflowIds?.join('|'),
          search,
        },
      }),
    enabled: Boolean(workflowIds.length > 0),
    select: (data: AxiosResponse<StepOptionsResponseDto[]>) => data.data,
  });
export const useGetStepConnections = (id?: string) =>
  useQuery({
    queryKey: [QueryKeys.getStepConnections, { stepId: id }],
    queryFn: () => getStepConnections(id!),
    enabled: Boolean(id),
    select: (data: AxiosResponse<StepConnectionsResponseDto>) => data.data,
  });
